import { useCallback, useEffect, useMemo, useState } from 'react';
import { Cloud, KeyRound, Loader2, Plus, Trash2 } from 'lucide-react';
import { mcpTokensApi, type CreatedMcpToken, type McpScope, type McpToken } from '@/api/mcp-tokens';
import { useAuth, useToast } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CopyId, PageHeader, Segmented, StatusPill } from '@/components/page';
import {
  API_URL,
  CodeBlock,
  ConfirmDialog,
  DevLabel,
  EmptyState,
  Hint,
  InlineError,
  KV,
  Mono,
  SecretDialog,
  SkeletonRows,
  Tag,
  ago,
  fmtDate,
  fmtDateTime,
} from '@/components/oauth';
import { cn } from '@/lib/utils';

const ENDPOINT = `${API_URL}/mcp`;

const SCOPES: [McpScope, string, string][] = [
  ['mcp', 'Connect', 'Open a session and list the available tools. Always included.'],
  ['mcp:read', 'Read', 'Status, lists, integration details, roles, users and audit logs.'],
  ['mcp:write', 'Write', 'Create and update organizations, apps, clients, roles and access grants.'],
  ['mcp:delete', 'Delete', 'Remove members, grants and clients; revoke tokens.'],
  ['mcp:admin', 'Admin', 'Rotate credentials and signing keys, reset passwords.'],
];
/** Scopes every key carries (the server requires mcp:read). */
const REQUIRED: McpScope[] = ['mcp', 'mcp:read'];

type PresetKey = 'observe' | 'operate' | 'manage';
const PRESETS: Record<PresetKey, { label: string; desc: string; scopes: McpScope[] }> = {
  observe: {
    label: 'Observe',
    desc: 'Read-only: status, lists, users, roles and audit logs.',
    scopes: ['mcp', 'mcp:read'],
  },
  operate: {
    label: 'Operate',
    desc: 'Observe, plus create and update organizations, apps, clients, roles and grants.',
    scopes: ['mcp', 'mcp:read', 'mcp:write'],
  },
  manage: {
    label: 'Manage',
    desc: 'Full parity, including credential rotation, password resets, removals and revocation.',
    scopes: ['mcp', 'mcp:read', 'mcp:write', 'mcp:delete', 'mcp:admin'],
  },
};

const presetOf = (scopes: McpScope[]): PresetKey | 'custom' =>
  (Object.keys(PRESETS) as PresetKey[]).find(
    (k) =>
      PRESETS[k].scopes.length === scopes.length && PRESETS[k].scopes.every((s) => scopes.includes(s))
  ) ?? 'custom';

const statusPill = (t: McpToken) =>
  t.status === 'revoked' ? (
    <StatusPill>Revoked</StatusPill>
  ) : t.status === 'expired' || (t.expiresAt && new Date(t.expiresAt) < new Date()) ? (
    <StatusPill tone="bad">Expired</StatusPill>
  ) : (
    <StatusPill tone="ok">Active</StatusPill>
  );

type SnipKey = 'json' | 'claude' | 'codex' | 'smoke';
const SNIP_LABELS: Record<SnipKey, string> = {
  json: 'Generic JSON',
  claude: 'Claude Code',
  codex: 'Codex TOML',
  smoke: 'Smoke test',
};

/** Placeholder-key snippets for the endpoint card. */
const placeholderSnippets = (key: string): Record<SnipKey, string> => ({
  json: JSON.stringify(
    { mcpServers: { authrz: { type: 'http', url: ENDPOINT, headers: { Authorization: `Bearer ${key}` } } } },
    null,
    2
  ),
  claude: `claude mcp add --transport http authrz ${ENDPOINT} \\\n  --header "Authorization: Bearer ${key}"`,
  codex: `[mcp_servers.authrz]\nurl = "${ENDPOINT}"\nbearer_token_env_var = "AUTHRZ_MCP_KEY"`,
  smoke: `curl -s ${ENDPOINT} \\\n  -H "Authorization: Bearer ${key}" \\\n  -H "Content-Type: application/json" \\\n  -H "Accept: application/json, text/event-stream" \\\n  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`,
});

function SnippetTabs({ snippets }: { snippets: Record<SnipKey, string> }) {
  const [tab, setTab] = useState<SnipKey>('json');
  return (
    <div className="space-y-2.5">
      <Segmented
        value={tab}
        onChange={setTab}
        options={(Object.keys(SNIP_LABELS) as SnipKey[]).map((k) => ({ value: k, label: SNIP_LABELS[k] }))}
      />
      <CodeBlock label={SNIP_LABELS[tab]} code={snippets[tab]} maxHeight={260} />
    </div>
  );
}

export function McpAccessPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<CreatedMcpToken | null>(null);
  const [detail, setDetail] = useState<McpToken | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<McpToken | null>(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setTokens(await mcpTokensApi.list());
    } catch (error) {
      setFailed(true);
      toast({
        variant: 'destructive',
        title: 'Could not load MCP keys',
        description: error instanceof Error ? error.message : 'Request failed',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await mcpTokensApi.revoke(revokeTarget.id);
      toast({ title: 'MCP key revoked', description: `${revokeTarget.name} can no longer call the endpoint.` });
      setRevokeTarget(null);
      await load();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not revoke MCP key',
        description: error instanceof Error ? error.message : 'Request failed',
      });
    } finally {
      setRevoking(false);
    }
  };

  const mainSnippets = useMemo(() => placeholderSnippets('$AUTHRZ_MCP_KEY'), []);

  if (!user?.is_super_admin) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <EmptyState icon={<KeyRound />} title="Super admin access required">
          Hosted MCP keys are managed by global super admins.
        </EmptyState>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            <Cloud className="h-3.5 w-3.5" />
            Always-on control plane
          </>
        }
        title="Hosted MCP"
        description="Scoped keys that let agents manage authrz.me over MCP. The endpoint runs in the cloud, so the desktop app doesn’t need to be open."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create MCP key
          </Button>
        }
      />

      <div className="space-y-5">
        <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-3.5 sm:px-[18px]">
            <h2 className="text-[15px] font-semibold tracking-tight">Endpoint</h2>
            <p className="mt-0.5 text-[13px] text-muted-foreground">
              Send the key as a bearer token. Keys are bound to your super admin account.
            </p>
          </div>
          <div className="space-y-4 p-4 sm:p-[18px]">
            <div className="space-y-1">
              <DevLabel>MCP endpoint</DevLabel>
              <CopyId value={ENDPOINT} />
            </div>
            <SnippetTabs snippets={mainSnippets} />
          </div>
        </section>

        <div className="grid gap-3 md:grid-cols-3">
          {(Object.keys(PRESETS) as PresetKey[]).map((k) => (
            <section key={k} className="rounded-lg border border-border bg-card p-4">
              <h3 className="text-[14.5px] font-semibold">{PRESETS[k].label}</h3>
              <p className="mt-1 text-[13px] text-muted-foreground">{PRESETS[k].desc}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {PRESETS[k].scopes
                  .filter((s) => s !== 'mcp')
                  .map((s) => (
                    <Tag key={s}>{s}</Tag>
                  ))}
              </div>
            </section>
          ))}
        </div>

        <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-3.5 sm:px-[18px]">
            <h2 className="text-[15px] font-semibold tracking-tight">Keys</h2>
            <p className="mt-0.5 text-[13px] text-muted-foreground">
              Raw key values are shown once at creation. Only the prefix is kept here.
            </p>
          </div>
          {loading ? (
            <SkeletonRows rows={3} />
          ) : failed ? (
            <EmptyState
              icon={<KeyRound />}
              title="Couldn’t load keys"
              action={
                <Button variant="outline" onClick={() => void load()}>
                  Try again
                </Button>
              }
            />
          ) : tokens.length === 0 ? (
            <EmptyState
              icon={<KeyRound />}
              title="No hosted MCP keys yet"
              action={
                <Button variant="outline" onClick={() => setCreateOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create MCP key
                </Button>
              }
            >
              Create one to connect an agent directly to the cloud endpoint.
            </EmptyState>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-24">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokens.map((t) => (
                  <TableRow
                    key={t.id}
                    className={cn('cursor-pointer', t.status === 'revoked' && 'opacity-60')}
                    onClick={(e) => {
                      if (!(e.target as HTMLElement).closest('button:not([data-row-open])')) setDetail(t);
                    }}
                  >
                    <TableCell className="min-w-[180px]">
                      <div className="grid gap-0.5">
                        <button
                          type="button"
                          data-row-open
                          onClick={() => setDetail(t)}
                          className="truncate text-left font-semibold hover:underline hover:underline-offset-4"
                        >
                          {t.name}
                        </button>
                        <span className="text-xs text-muted-foreground">Created {fmtDate(t.createdAt)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Mono>{t.prefix}…</Mono>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {t.scopes.filter((s) => s !== 'mcp').length === 0 ? (
                          <span className="text-muted-foreground">connect only</span>
                        ) : (
                          t.scopes
                            .filter((s) => s !== 'mcp')
                            .map((s) => <Tag key={s}>{s.replace('mcp:', '')}</Tag>)
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{statusPill(t)}</TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-[12.5px] tabular-nums">
                      {ago(t.lastUsedAt)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-[12.5px] tabular-nums">
                      {t.expiresAt ? fmtDate(t.expiresAt) : 'Never'}
                    </TableCell>
                    <TableCell>
                      {t.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setRevokeTarget(t)}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>
      </div>

      <CreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={async (result) => {
          setCreateOpen(false);
          setCreated(result);
          await load();
        }}
      />

      {/* one-time key: cannot be dismissed until acknowledged */}
      <SecretDialog
        open={!!created}
        title="Store this key now"
        description="authrz.me never shows the raw value again. Put it in an environment variable or a secret manager."
        ackLabel="I stored this key somewhere safe"
        onDone={() => {
          toast({ title: 'MCP key created', description: created ? `${created.token.name} · ${created.token.prefix}…` : undefined });
          setCreated(null);
        }}
      >
        {created && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <b className="text-sm font-semibold">{created.token.name}</b>
              <span className="ml-auto flex flex-wrap gap-1">
                {created.token.scopes
                  .filter((s) => s !== 'mcp')
                  .map((s) => (
                    <Tag key={s}>{s.replace('mcp:', '')}</Tag>
                  ))}
              </span>
            </div>
            <div className="space-y-1">
              <DevLabel>MCP key</DevLabel>
              <CopyId
                value={created.rawKey}
                className="w-full py-1.5 text-[13px] [&>span]:whitespace-normal [&>span]:break-all"
              />
            </div>
            <SnippetTabs
              snippets={{
                json: created.configurations.genericJson,
                claude: created.configurations.claudeCode,
                codex: created.configurations.codexToml,
                smoke: created.configurations.smoke,
              }}
            />
          </div>
        )}
      </SecretDialog>

      {/* key sheet */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-foreground/[0.04]">
                    <KeyRound className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 truncate">{detail.name}</span>
                </DialogTitle>
                <DialogDescription>
                  <Mono>{detail.prefix}…</Mono>
                </DialogDescription>
              </DialogHeader>
              <KV
                rows={[
                  ['Status', statusPill(detail)],
                  ['Access', presetOf(detail.scopes) === 'custom' ? 'Custom' : PRESETS[presetOf(detail.scopes) as PresetKey].label],
                  ['Created', <span key="c" className="font-mono text-[12.5px]">{fmtDateTime(detail.createdAt)}</span>],
                  [
                    'Last used',
                    <span key="l" className="font-mono text-[12.5px]">
                      {detail.lastUsedAt ? `${fmtDateTime(detail.lastUsedAt)} · ${ago(detail.lastUsedAt)}` : 'Never'}
                    </span>,
                  ],
                  ['Expires', <span key="e" className="font-mono text-[12.5px]">{detail.expiresAt ? fmtDateTime(detail.expiresAt) : 'Never'}</span>],
                  ['Key ID', <CopyId key="id" value={detail.id} />],
                ]}
              />
              <div className="space-y-1.5">
                <DevLabel>Scopes</DevLabel>
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {SCOPES.map(([s, , desc]) => (
                    <li key={s} className="flex items-center gap-3 px-3 py-2.5">
                      <span className="min-w-0 flex-1">
                        <b className="font-mono text-[12.5px]">{s}</b>
                        <span className="block text-xs text-muted-foreground">{desc}</span>
                      </span>
                      {detail.scopes.includes(s) ? <StatusPill tone="ok">Granted</StatusPill> : <StatusPill>Not granted</StatusPill>}
                    </li>
                  ))}
                </ul>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                {detail.status === 'active' && (
                  <Button
                    variant="outline"
                    className="text-destructive hover:text-destructive sm:mr-auto"
                    onClick={() => {
                      const t = detail;
                      setDetail(null);
                      setRevokeTarget(t);
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Revoke key
                  </Button>
                )}
                <Button variant="outline" onClick={() => setDetail(null)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(o) => !o && setRevokeTarget(null)}
        danger
        title={`Revoke “${revokeTarget?.name ?? ''}”?`}
        body={
          <>
            Agents using <Mono>{revokeTarget?.prefix}…</Mono> lose access on their next request.
            Revoked keys can’t be restored.
          </>
        }
        confirmLabel="Revoke key"
        pendingLabel="Revoking…"
        pending={revoking}
        onConfirm={handleRevoke}
      />
    </div>
  );
}

/* ── create ─────────────────────────────────────────────── */

function CreateDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (result: CreatedMcpToken) => void | Promise<void>;
}) {
  const { toast } = useToast();
  const [name, setName] = useState('Agent control plane');
  const [nameErr, setNameErr] = useState('');
  const [scopes, setScopes] = useState<McpScope[]>(PRESETS.operate.scopes);
  const [expiry, setExpiry] = useState('90');
  const [customDate, setCustomDate] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      setName('Agent control plane');
      setNameErr('');
      setScopes(PRESETS.operate.scopes);
      setExpiry('90');
      setCustomDate('');
    }
  }, [open]);

  const current = presetOf(scopes);

  const submit = async () => {
    if (!name.trim()) {
      setNameErr('Enter a name.');
      return;
    }
    let expiresAt: string | undefined;
    if (expiry === 'custom') {
      if (!customDate) {
        toast({ variant: 'destructive', title: 'Pick an expiry date' });
        return;
      }
      expiresAt = new Date(customDate).toISOString();
    } else if (expiry !== 'never') {
      expiresAt = new Date(Date.now() + Number(expiry) * 864e5).toISOString();
    }
    setCreating(true);
    try {
      const result = await mcpTokensApi.create({ name: name.trim(), scopes, ...(expiresAt ? { expiresAt } : {}) });
      await onCreated(result);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not create MCP key',
        description: error instanceof Error ? error.message : 'Request failed',
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !creating && onOpenChange(o)}>
      <DialogContent className="max-w-xl">
        <form
          noValidate
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <DialogHeader>
            <DialogTitle>Create hosted MCP key</DialogTitle>
            <DialogDescription>
              The key acts as your super admin account, limited to the scopes you choose.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <DevLabel htmlFor="mName">Key name</DevLabel>
            <Input
              id="mName"
              value={name}
              maxLength={80}
              autoComplete="off"
              aria-invalid={nameErr ? true : undefined}
              onChange={(e) => {
                setName(e.target.value);
                setNameErr('');
              }}
              onFocus={(e) => e.currentTarget.select()}
            />
            <Hint>Where it’s used, so you can find it later.</Hint>
            <InlineError>{nameErr}</InlineError>
          </div>
          <div className="space-y-1.5">
            <DevLabel>Access</DevLabel>
            <Segmented
              value={current}
              onChange={(v) => {
                if (v !== 'custom') setScopes(PRESETS[v].scopes);
              }}
              options={[
                ...(Object.keys(PRESETS) as PresetKey[]).map((k) => ({ value: k as PresetKey | 'custom', label: PRESETS[k].label })),
                { value: 'custom' as const, label: 'Custom' },
              ]}
            />
          </div>
          <fieldset className="grid gap-2">
            <legend className="sr-only">Scopes</legend>
            {SCOPES.map(([s, , desc]) => {
              const locked = REQUIRED.includes(s);
              return (
                <label
                  key={s}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border border-border p-3',
                    locked ? 'cursor-default' : 'cursor-pointer hover:border-foreground/30'
                  )}
                >
                  <Checkbox
                    className="mt-0.5"
                    checked={scopes.includes(s)}
                    disabled={locked}
                    onCheckedChange={() =>
                      setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))
                    }
                  />
                  <span className="min-w-0">
                    <b className="font-mono text-[12.5px] font-semibold">{s}</b>
                    <small className="block text-[12.5px] text-muted-foreground">{desc}</small>
                  </span>
                </label>
              );
            })}
          </fieldset>
          <div className="space-y-1.5">
            <DevLabel htmlFor="mExp">Expires</DevLabel>
            <Select value={expiry} onValueChange={setExpiry}>
              <SelectTrigger id="mExp">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">In 30 days</SelectItem>
                <SelectItem value="90">In 90 days</SelectItem>
                <SelectItem value="custom">On a date…</SelectItem>
                <SelectItem value="never">Never</SelectItem>
              </SelectContent>
            </Select>
            {expiry === 'custom' && (
              <Input
                type="datetime-local"
                aria-label="Expiry date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
              />
            )}
            <Hint>Keys without an expiry stay valid until revoked.</Hint>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating}>
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {creating ? 'Creating…' : 'Create key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
